import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  ModernButton,
  Label,
  Input,
  DetailPageSkeleton,
  Toast,
  ToastType,
  ModernTextarea,
} from '../../components/ui'
import { PhoneInput } from '../../components/form/PhoneInput'
import {
  BrazilAddressFields,
  BrazilAddressValue,
} from '../../components/address/BrazilAddressFields'
import { apiService, CustomerRecord } from '../../services/api'
import { lookupZipCode } from '../../services/cepLookup'
import { formatCep, formatCpf, formatCnpj, digitsOnly } from '../../utils/brFormat'

const emptyAddress = (): BrazilAddressValue => ({
  zipCode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
})

function addressFromCustomer(c: CustomerRecord): BrazilAddressValue {
  const pa = c.primaryAddress
  if (pa) {
    return {
      zipCode: formatCep(pa.zipCode || ''),
      street: pa.street || '',
      number: pa.number || '',
      complement: pa.complement || '',
      neighborhood: pa.neighborhood || '',
      city: pa.city || '',
      state: (pa.state || '').toUpperCase().slice(0, 2),
    }
  }
  if (c.address?.trim()) {
    return {
      ...emptyAddress(),
      street: c.address.trim(),
      number: 'S/N',
    }
  }
  return emptyAddress()
}

function formatLeadDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const loc = locale.startsWith('en') ? 'en-US' : locale.startsWith('es') ? 'es-ES' : 'pt-BR'
  return d.toLocaleString(loc)
}

export const CustomerEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [customer, setCustomer] = useState<CustomerRecord | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')
  const [addr, setAddr] = useState<BrazilAddressValue>(emptyAddress)
  const [zipLoading, setZipLoading] = useState(false)
  const [zipFieldError, setZipFieldError] = useState<string | null>(null)
  const [addrFieldsLockedFromCep, setAddrFieldsLockedFromCep] = useState(false)
  const lastSuccessfulCepRef = useRef('')

  const docLabel =
    customer?.documentType === 'cnpj'
      ? t('customers.edit.documentCnpj')
      : t('customers.edit.documentCpf')

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const c = await apiService.getCustomerById(id)
      setCustomer(c)
      setName(c.name ?? '')
      setEmail(c.email ?? '')
      setPhone(c.phone ?? '')
      setNotes(c.notes ?? '')
      setAddr(addressFromCustomer(c))
      lastSuccessfulCepRef.current = ''
      setAddrFieldsLockedFromCep(false)
    } catch {
      setToast({ message: t('customers.edit.loadError'), type: 'error' })
      setCustomer(null)
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    void load()
  }, [load])

  const applyAddrChange = useCallback((next: BrazilAddressValue) => {
    if (digitsOnly(next.zipCode) !== lastSuccessfulCepRef.current) {
      setAddrFieldsLockedFromCep(false)
    }
    setAddr(next)
  }, [])

  const runCepLookup = useCallback(
    async (strictIncomplete: boolean) => {
      const normalized = digitsOnly(addr.zipCode)
      if (normalized.length !== 8) {
        if (strictIncomplete) {
          setZipFieldError(t('customers.address.zipInvalid'))
        }
        return
      }
      setZipFieldError(null)
      try {
        setZipLoading(true)
        const res = await lookupZipCode(normalized, { publicOnly: false })
        lastSuccessfulCepRef.current = normalized
        setAddrFieldsLockedFromCep(true)
        setAddr(prev => ({
          ...prev,
          zipCode: formatCep(res.zipCode || normalized),
          street: (res.street && res.street.trim()) || prev.street,
          neighborhood: (res.neighborhood && res.neighborhood.trim()) || prev.neighborhood,
          city: (res.city && res.city.trim()) || prev.city,
          state:
            (res.state && res.state.trim().toUpperCase().slice(0, 2)) ||
            prev.state.toUpperCase().slice(0, 2),
          complement:
            prev.complement?.trim() ||
            (res.complement && res.complement.trim()) ||
            '',
        }))
      } catch (e: unknown) {
        lastSuccessfulCepRef.current = ''
        setAddrFieldsLockedFromCep(false)
        const msg = e instanceof Error ? e.message : t('customers.address.zipLookupError')
        setZipFieldError(msg)
      } finally {
        setZipLoading(false)
      }
    },
    [addr.zipCode, t],
  )

  const handleSave = async () => {
    if (!id || !customer) return
    setSaving(true)
    try {
      const z = digitsOnly(addr.zipCode)
      const requiredAddr =
        addr.street.trim() &&
        addr.number.trim() &&
        addr.neighborhood.trim() &&
        addr.city.trim() &&
        addr.state.length === 2 &&
        z.length === 8
      await apiService.updateCustomer(id, {
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        notes: notes.trim() || undefined,
        primaryAddress: requiredAddr
          ? {
              street: addr.street.trim(),
              number: addr.number.trim(),
              complement: addr.complement.trim() || undefined,
              neighborhood: addr.neighborhood.trim(),
              city: addr.city.trim(),
              state: addr.state.trim().toUpperCase().slice(0, 2),
              zipCode: z,
            }
          : undefined,
      })
      setToast({ message: t('customers.edit.saveSuccess'), type: 'success' })
      setTimeout(() => navigate(-1), 600)
    } catch {
      setToast({ message: t('customers.edit.saveError'), type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <DetailPageSkeleton />
  }

  if (!customer) {
    return (
      <div className="p-6">
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}
        <ModernButton variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('financialSales.fiscalExport.customerEdit.back')}
        </ModernButton>
      </div>
    )
  }

  const docDisplay =
    customer.documentType === 'cnpj'
      ? formatCnpj(customer.document)
      : formatCpf(customer.document)

  const origin = customer.originLead
  const originStatusLabel = origin
    ? t(`leads.status.${origin.status}`, { defaultValue: origin.status })
    : ''

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      <div className="flex items-center gap-4">
        <ModernButton variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('financialSales.fiscalExport.customerEdit.back')}
        </ModernButton>
        <h1 className="text-xl font-semibold">{t('customers.edit.title')}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{customer.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <p className="text-sm font-medium text-foreground">{t('customers.edit.sectionContact')}</p>
            <div className="space-y-2">
              <Label htmlFor="cust-name">{t('customers.edit.name')}</Label>
              <Input id="cust-name" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-doc">{docLabel}</Label>
              <Input id="cust-doc" value={docDisplay} readOnly disabled className="bg-muted/50" />
              <p className="text-xs text-muted-foreground">{t('customers.edit.documentReadOnlyHint')}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-email">{t('customers.edit.email')}</Label>
              <Input
                id="cust-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-phone">{t('customers.edit.phone')}</Label>
              <PhoneInput value={phone} onChange={setPhone} />
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">{t('customers.edit.sectionAddress')}</p>
            <BrazilAddressFields
              value={addr}
              onChange={applyAddrChange}
              onZipBlur={() => void runCepLookup(false)}
              onZipSearchPress={() => void runCepLookup(true)}
              isLoadingZip={zipLoading}
              zipError={zipFieldError}
              lockDerivedAddressFields={addrFieldsLockedFromCep}
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">{t('customers.edit.sectionNotes')}</p>
            <ModernTextarea
              id="cust-notes"
              label={t('customers.edit.notes')}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
            />
          </div>

          <ModernButton onClick={() => void handleSave()} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('customers.edit.save')}
          </ModernButton>
        </CardContent>
      </Card>

      {origin ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('customers.edit.sectionFunnelOrigin')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <p className="text-muted-foreground">{t('customers.edit.originLeadName')}</p>
                <p className="font-medium">{origin.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('customers.edit.originLeadSource')}</p>
                <p className="font-medium">{origin.source}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('customers.edit.originLeadStatus')}</p>
                <p className="font-medium">{originStatusLabel}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('customers.edit.originLeadCreatedAt')}</p>
                <p className="font-medium">
                  {formatLeadDateTime(origin.createdAt, i18n.language) || '—'}
                </p>
              </div>
              {origin.convertedAt ? (
                <div>
                  <p className="text-muted-foreground">{t('customers.edit.originLeadConvertedAt')}</p>
                  <p className="font-medium">
                    {formatLeadDateTime(origin.convertedAt, i18n.language) || '—'}
                  </p>
                </div>
              ) : null}
            </div>
            {origin.message?.trim() ? (
              <div>
                <p className="text-muted-foreground mb-1">{t('customers.edit.originLeadMessage')}</p>
                <p className="whitespace-pre-wrap rounded-md border border-border/60 bg-muted/30 p-3">
                  {origin.message.trim()}
                </p>
              </div>
            ) : null}
            {origin.notes?.trim() ? (
              <div>
                <p className="text-muted-foreground mb-1">{t('customers.edit.originLeadNotes')}</p>
                <p className="whitespace-pre-wrap rounded-md border border-border/60 bg-muted/30 p-3">
                  {origin.notes.trim()}
                </p>
              </div>
            ) : null}
            <ModernButton
              variant="outline"
              type="button"
              onClick={() => navigate(`/leads/edit/${origin.id}`)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('customers.edit.openOriginLead')}
            </ModernButton>
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
